import { useCallback, useContext, useEffect, useRef } from "react"

import EventEmitter from "src/EventEmitter"

import CloudroomContext from "../CloudroomContext"

// Manages sharing/un-sharing the local user's own screen. Used independently
// by whichever layout renders a "share your screen" control (Student or Tutor).
const useLocalScreenShare = () => {
  const {
    audioVideo,
    roster,
    session,
    localUser,
    muteLocalVideo,
    unmuteLocalVideo,
  } = useContext(CloudroomContext)
  const videoOnBeforeScreenShareRef = useRef(false)

  const startLocalScreenShare = useCallback(
    async (onError?: () => void) => {
      const videoIsOn =
        roster[session.chimeJoinInfo.attendee.attendeeId]?.sharingVideo
      videoOnBeforeScreenShareRef.current = videoIsOn

      muteLocalVideo()
      await audioVideo
        .startContentShareFromScreenCapture()
        .then(stream => audioVideo.startContentShare(stream))
        .catch(() => {
          onError?.()
          if (videoOnBeforeScreenShareRef.current) unmuteLocalVideo()
        })
    },
    [audioVideo, muteLocalVideo, roster, session, unmuteLocalVideo]
  )

  const stopLocalScreenShare = useCallback(() => {
    audioVideo.stopContentShare()
  }, [audioVideo])

  useEffect(() => {
    const onEndScreenShare = data => {
      if (data.userId !== localUser.id) return

      stopLocalScreenShare()
    }
    EventEmitter.subscribe("end_screen_share", onEndScreenShare)

    return () => EventEmitter.unsubscribe("end_screen_share", onEndScreenShare)
  }, [stopLocalScreenShare, localUser.id])

  // observer to restart video (if applicable) after screen share
  useEffect(() => {
    if (!audioVideo) return

    const restartVideoObserver = {
      contentShareDidStop: () => {
        if (videoOnBeforeScreenShareRef.current) unmuteLocalVideo()
      },
    }

    audioVideo.addContentShareObserver(restartVideoObserver)

    return () => audioVideo.removeContentShareObserver(restartVideoObserver)
  }, [audioVideo, unmuteLocalVideo])

  return { startLocalScreenShare, stopLocalScreenShare }
}

export default useLocalScreenShare
